import { isNil } from "lodash";
import { type FC, type KeyboardEvent, memo } from "react";

import { formatFileSize } from "../../utils/files";
import { Button } from "../button";
import { Icon } from "../icon";
import { Spinner } from "../loading";
import { type EmailDraftAttachment, useEmailDraft } from "./email-draft-context";

const focusAttachment = (attachment?: EmailDraftAttachment) => {
  const element = document.querySelector(`[data-attachment-id="${attachment?.id}"]`);

  if (element) {
    (element as HTMLElement).focus();
  }
};

export const EmailDraftAttachments: FC = memo(() => {
  const { attachments, handleDownloadAttachment, handleDeleteAttachment, isEditable } = useEmailDraft();

  if (!attachments.length) {
    return null;
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>, attachment: EmailDraftAttachment) => {
    const currentAttachmentIndex = attachments.findIndex((a) => a.id === attachment.id);

    if (e.key === "Enter") {
      e.preventDefault();
      handleDownloadAttachment(attachment.id);
    }

    if (e.key === "Backspace") {
      e.preventDefault();

      if (!isEditable || attachment.pending || !attachment.removable) {
        return;
      }

      const nextAttachmentIndex =
        currentAttachmentIndex === attachments.length - 1 ? attachments.length - 2 : currentAttachmentIndex + 1;
      focusAttachment(attachments[nextAttachmentIndex]);

      handleDeleteAttachment(attachment.id);
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      focusAttachment(attachments[currentAttachmentIndex === attachments.length - 1 ? 0 : currentAttachmentIndex + 1]);
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      focusAttachment(attachments[currentAttachmentIndex === 0 ? attachments.length - 1 : currentAttachmentIndex - 1]);
    }
  };

  return (
    <div className="flex flex-row flex-wrap gap-1 pb-2">
      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          tabIndex={0}
          onKeyDown={(e) => handleKeyDown(e, attachment)}
          data-attachment-id={attachment.id}
          className="min-w-2/3 max-w-full bg-secondary/50 p-1 pl-2 rounded-md flex flex-grow-0 flex-shrink-0 items-center gap-2 text-sm ring-offset-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          <Icon icon="file_present" className="flex-none text-muted-foreground" />

          <Button
            variant="link"
            onClick={() => handleDownloadAttachment(attachment.id)}
            tabIndex={-1}
            className="p-0 min-w-0 no-underline! truncate block flex-1 text-start"
          >
            {attachment.name}
          </Button>

          {!isNil(attachment.size) && (
            <span className="text-muted-foreground text-nowrap flex-none">({formatFileSize(attachment.size)})</span>
          )}

          {attachment.removable && (
            <Button
              variant="ghost"
              size="xs"
              display="icon"
              onClick={() => handleDeleteAttachment(attachment.id)}
              disabled={attachment.pending || !isEditable}
              tabIndex={-1}
              className="-ml-1 flex-none"
            >
              {attachment.pending ? <Spinner className="size-3" /> : <Icon icon="delete" />}
            </Button>
          )}
        </div>
      ))}
    </div>
  );
});
EmailDraftAttachments.displayName = "EmailDraftAttachments";
